import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// The funnel event names the SPA is allowed to report. Must stay in sync with the server-side
// AnalyticsEvents allow-list; anything else is silently dropped by the ingest endpoint.
export type AnalyticsEvent =
  | 'visit'
  | 'welcome_shown'
  | 'welcome_dismissed'
  | 'nudge_shown'
  | 'nudge_dismissed'
  | 'signin_started';

// Emits first-party signup-funnel events so we can measure how many visitors actually reach and
// complete registration. Deliberately privacy-light: the only identifier is a random per-tab id
// held in sessionStorage, so there is no cross-site tracking and nothing personal leaves the
// browser. Every call is fire-and-forget — analytics must never delay or break the UI.
@singleton()
export class AnalyticsService {
  private readonly http: HttpClient = resolve(HttpClient);

  private static readonly sessionKey = 'tc.analyticsSession';

  // Events already sent this session, so repeated calls (e.g. a nudge reopening) do not inflate
  // the counts for steps that are only meaningful once per visit.
  private readonly sent = new Set<AnalyticsEvent>();

  // Returns the per-session id, creating one on first use. Falls back to an in-memory id when
  // storage is unavailable (private mode), which simply means the session is not deduplicated
  // across reloads.
  private sessionId(): string {
    try {
      const existing = window.sessionStorage.getItem(AnalyticsService.sessionKey);
      if (existing) return existing;
      const created = AnalyticsService.newId();
      window.sessionStorage.setItem(AnalyticsService.sessionKey, created);
      return created;
    } catch {
      this.fallbackId ??= AnalyticsService.newId();
      return this.fallbackId;
    }
  }

  private fallbackId: string | null = null;

  private static newId(): string {
    try {
      return crypto.randomUUID().replace(/-/g, '');
    } catch {
      return Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  }

  // Records one funnel event. Never throws and never awaits anything the caller depends on.
  track(event: AnalyticsEvent): void {
    void this.send(event);
  }

  // Records an event at most once per browser session. Used for the "did this visitor see X"
  // steps, where a second occurrence says nothing new about the funnel.
  trackOnce(event: AnalyticsEvent): void {
    if (this.sent.has(event)) return;
    this.sent.add(event);
    void this.send(event);
  }

  private async send(event: AnalyticsEvent): Promise<void> {
    try {
      // keepalive lets the request outlive the page: `signin_started` is emitted immediately
      // before a full-page redirect to Auth0, and without this the browser would cancel it and
      // the most important funnel step would never be recorded.
      await this.http.postWithCsrf('/api/analytics/events', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, sessionId: this.sessionId() }),
        keepalive: true
      });
    } catch {
      // Non-fatal by design: a lost analytics event must never surface to the user.
    }
  }
}
